import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, TextField, Button, Typography, Alert,
  CircularProgress, Link, Divider
} from '@mui/material';
import { Security as SecurityIcon } from '@mui/icons-material';
import WebApi from '../services/webApi';
import { IS_WEB } from '../services/platform';
import { hasTrialIntent } from '../shared/trialIntent';
import { readVerificationToken, VerifyEmailPage } from './EmailVerification';

// Web builds only: everything behind this gate requires a signed-in account
// (the account is where the birds live). Desktop and dev builds render
// children untouched.

const WebAuthContext = createContext({ email: null, signOut: async () => {} });
export const useWebAuth = () => useContext(WebAuthContext);

const readResetToken = () => {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('reset');
  return token && /^[a-f0-9]{64}$/.test(token) ? token : null;
};

const AuthForm = ({ onSignedIn }) => {
  // view: 'signin' | 'signup' | 'forgot' | 'reset'
  const trialIntent = hasTrialIntent();
  const params = new URLSearchParams(window.location.search);
  const emailJustVerified = params.get('email-verified') === '1';
  const [view, setView] = useState(readResetToken() ? 'reset' : emailJustVerified || params.get('signin') === '1' ? 'signin' : trialIntent ? 'signup' : 'signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(emailJustVerified ? 'Your email is verified. Sign in to continue to your account.' : '');

  const switchView = (next) => {
    setView(next);
    setError('');
    setNotice('');
    setPassword('');
    setConfirm('');
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    setNotice('');
    try {
      if (view === 'signin') {
        const r = await WebApi.login(email, password);
        onSignedIn(r.user.email);
      } else if (view === 'signup') {
        if (password !== confirm) {
          setError('Passwords do not match.');
          return;
        }
        const r = await WebApi.signup(email, password);
        onSignedIn(r.user.email);
      } else if (view === 'forgot') {
        const r = await WebApi.requestReset(email);
        setNotice(r.resetConfigured
          ? 'If an account exists for that email, a reset link is on its way. Check your inbox (and spam folder).'
          : 'Password resets by email are not available yet — please contact support@pigeonpedigreemanager.com and we will reset it for you.');
      } else if (view === 'reset') {
        if (password !== confirm) {
          setError('Passwords do not match.');
          return;
        }
        const r = await WebApi.resetPassword(readResetToken(), password);
        window.history.replaceState(null, '', window.location.pathname);
        onSignedIn(r.user.email);
      }
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const title =
    view === 'signup' ? 'Create your free account' :
    view === 'forgot' ? 'Reset your password' :
    view === 'reset' ? 'Choose a new password' : 'Sign in';

  const buttonLabel =
    view === 'signup' ? (trialIntent ? 'Continue to free trial' : 'Create account') :
    view === 'forgot' ? 'Send reset link' :
    view === 'reset' ? 'Set new password' : 'Sign in';

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100dvh"
      bgcolor="background.default"
      p={2}
    >
      <Card sx={{ maxWidth: 420, width: '100%', borderRadius: 2 }}>
        <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
          <Box textAlign="center" mb={3}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
              <SecurityIcon sx={{ fontSize: 32, color: 'primary.main', mr: 1 }} />
              <Typography variant="h5" component="div" color="primary.main" fontWeight="bold">
                Pigeon Pedigree Manager
              </Typography>
            </Box>
            <Typography variant="h6">{title}</Typography>
            {view === 'signup' && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {trialIntent
                  ? 'Create your account, verify your email, then start your 7-day free trial. No card required.'
                  : 'Create an account and verify your email to start your 7-day free trial. Use your loft on your phone or computer. No card required.'}
              </Typography>
            )}
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          {notice && <Alert severity="info" sx={{ mb: 2 }}>{notice}</Alert>}

          <form onSubmit={submit}>
            <Box display="flex" flexDirection="column" gap={2}>
              {view !== 'reset' && (
                <TextField
                  fullWidth
                  type="email"
                  label="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={busy}
                  autoComplete="email"
                  required
                />
              )}
              {view !== 'forgot' && (
                <TextField
                  fullWidth
                  type="password"
                  label={view === 'reset' ? 'New password' : 'Password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={busy}
                  autoComplete={view === 'signin' ? 'current-password' : 'new-password'}
                  required
                  inputProps={{ minLength: view === 'signup' || view === 'reset' ? 8 : undefined }}
                  helperText={view === 'signup' || view === 'reset' ? 'At least 8 characters' : undefined}
                />
              )}
              {(view === 'signup' || view === 'reset') && (
                <TextField
                  fullWidth
                  type="password"
                  label="Confirm password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  disabled={busy}
                  autoComplete="new-password"
                  required
                />
              )}
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={busy}
                startIcon={busy ? <CircularProgress size={18} color="inherit" /> : null}
              >
                {buttonLabel}
              </Button>
            </Box>
          </form>

          <Divider sx={{ my: 2 }} />

          <Box display="flex" flexDirection="column" gap={1} textAlign="center">
            {view === 'signin' && (
              <>
                <Typography variant="body2">
                  New here?{' '}
                  <Link component="button" type="button" onClick={() => switchView('signup')}>
                    Create an account
                  </Link>
                </Typography>
                <Typography variant="body2">
                  <Link component="button" type="button" onClick={() => switchView('forgot')}>
                    Forgot your password?
                  </Link>
                </Typography>
              </>
            )}
            {view !== 'signin' && (
              <Typography variant="body2">
                <Link component="button" type="button" onClick={() => switchView('signin')}>
                  Back to sign in
                </Link>
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export function WebAuthGate({ children }) {
  // Non-web builds return children before status is ever read.
  const [status, setStatus] = useState('checking');
  const [email, setEmail] = useState(null);
  const [verificationToken, setVerificationToken] = useState(() => IS_WEB ? readVerificationToken() : null);

  useEffect(() => {
    if (!IS_WEB) return undefined;
    const readLink = () => setVerificationToken(readVerificationToken());
    window.addEventListener('hashchange', readLink);
    window.addEventListener('popstate', readLink);
    return () => {
      window.removeEventListener('hashchange', readLink);
      window.removeEventListener('popstate', readLink);
    };
  }, []);

  useEffect(() => {
    if (!IS_WEB) return undefined;
    let cancelled = false;
    setStatus('checking');
    if (readResetToken() || verificationToken !== null) {
      // A reset link must always show the reset form — even in a browser
      // with a live session (the user may be resetting for another device).
      setStatus('signedout');
    } else {
      WebApi.me()
        .then((r) => { if (!cancelled) { setEmail(r.user.email); setStatus('signedin'); } })
        .catch(() => { if (!cancelled) setStatus('signedout'); });
    }

    // Fired by the API client on any 401 — the session died under us.
    const onAuthLost = () => {
      WebApi.resetCache();
      setEmail(null);
      setStatus('signedout');
    };
    window.addEventListener('ppm-auth-lost', onAuthLost);
    return () => {
      cancelled = true;
      window.removeEventListener('ppm-auth-lost', onAuthLost);
    };
  }, [verificationToken]);

  const signOut = useCallback(async () => {
    try { await WebApi.logout(); } catch (e) { /* clearing locally regardless */ }
    // Full reload guarantees no per-account state (caches, contexts) survives.
    window.location.reload();
  }, []);

  if (!IS_WEB) return children;
  if (verificationToken !== null) return <VerifyEmailPage key={verificationToken} token={verificationToken} />;

  if (status === 'checking') {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (status === 'signedout') {
    return <AuthForm onSignedIn={(e) => { setEmail(e); setStatus('signedin'); }} />;
  }

  return (
    <WebAuthContext.Provider value={{ email, signOut }}>
      {children}
    </WebAuthContext.Provider>
  );
}

export default WebAuthGate;
